import React, { useEffect, useState } from 'react';
import { Node } from 'reactflow';
import { X, Trash2, Square, ChevronRight } from 'lucide-react';
import { TechNodeData, TextNodeData, NodeSizeMod } from '../../types';
import { TechIcon, getIconId } from '../../lib/icons';
import { Tooltip } from '@/components/ui/beui';

type NodeData = TechNodeData | TextNodeData;

interface NodeSettingsPanelProps {
  node: Node<NodeData> | null;
  onUpdate: (id: string, updates: Partial<Node<NodeData>>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  /** Open the icon picker for this node (tech nodes only). */
  onChangeIcon?: () => void;
}

// Colors with light/dark variants for better theme support
const NODE_COLORS = [
  { label: 'Default', value: '', lightBg: '#ffffff', darkBg: '#1e293b' },
  { label: 'Slate', value: 'f1f5f9', lightBg: '#f1f5f9', darkBg: '#334155' },
  { label: 'Blue', value: 'dbeafe', lightBg: '#dbeafe', darkBg: '#1e3a5f' },
  { label: 'Green', value: 'dcfce7', lightBg: '#dcfce7', darkBg: '#14532d' },
  { label: 'Purple', value: 'f3e8ff', lightBg: '#f3e8ff', darkBg: '#3b0764' },
  { label: 'Amber', value: 'fef3c7', lightBg: '#fef3c7', darkBg: '#78350f' },
  { label: 'Rose', value: 'ffe4e6', lightBg: '#ffe4e6', darkBg: '#4c0519' },
  { label: 'Cyan', value: 'cffafe', lightBg: '#cffafe', darkBg: '#164e63' },
];

const BORDER_COLORS = [
  { label: 'Default', value: '' },
  { label: 'Gray', value: '64748b' },
  { label: 'Blue', value: '3b82f6' },
  { label: 'Green', value: '22c55e' },
  { label: 'Red', value: 'ef4444' },
  { label: 'Purple', value: 'a855f7' },
  { label: 'Orange', value: 'f97316' },
  { label: 'Amber', value: 'f59e0b' },
];

const SIZE_OPTIONS: { label: string; value: NodeSizeMod }[] = [
  { label: 'S', value: 'small' },
  { label: 'M', value: 'default' },
  { label: 'L', value: 'large' },
];

const NodeSettingsPanel: React.FC<NodeSettingsPanelProps> = ({
  node,
  onUpdate,
  onDelete,
  onClose,
  onChangeIcon,
}) => {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (node) {
      setLabel(node.data.label);
      setDescription(node.data.description || '');
    }
    // Intentionally keyed on node?.id only: reset the label field solely when a
    // different node is selected, not on every node-data update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id]);

  if (!node) return null;

  const showBorder = node.data.showBorder !== false;
  const currentNodeColor = node.data.nodeColor || '';
  const currentBorderColor = node.data.borderColor || '';
  const currentSizeMod = node.data.sizeMod || 'default';
  const isTechNode = node.type === 'techNode';
  const iconId = isTechNode ? getIconId(node.data as TechNodeData) : '';
  const iconHex = (node.data as TechNodeData).hex || '64748b';

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
  };

  const submitLabel = () => {
    if (label !== node.data.label) {
      onUpdate(node.id, {
        data: { ...node.data, label: label.trim() || node.data.label },
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      submitLabel();
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleBorderToggle = () => {
    onUpdate(node.id, {
      data: { ...node.data, showBorder: !showBorder },
    });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const submitDescription = () => {
    const next = description.trim() || undefined;
    if (next !== node.data.description) {
      onUpdate(node.id, {
        data: { ...node.data, description: next },
      });
    }
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      submitDescription();
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleNodeColorChange = (color: string) => {
    onUpdate(node.id, {
      data: { ...node.data, nodeColor: color },
    });
  };

  const handleBorderColorChange = (color: string) => {
    onUpdate(node.id, {
      data: { ...node.data, borderColor: color },
    });
  };

  const handleSizeChange = (size: NodeSizeMod) => {
    onUpdate(node.id, {
      data: { ...node.data, sizeMod: size },
    });
  };

  return (
    <div className="absolute top-20 left-4 z-20 w-64 rounded-xl shadow-2xl border backdrop-blur-md animate-in slide-in-from-left-2 transition-colors bg-card/90 border-border dark:bg-card/90 dark:border-border">
      <div className="flex items-center justify-between p-3 border-b border-border dark:border-border">
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Node Settings
        </h3>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-muted-foreground dark:hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 space-y-4">
        {/* Icon (tech nodes only) */}
        {isTechNode && onChangeIcon && (
          <div>
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Icon
            </label>
            <button
              onClick={onChangeIcon}
              className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded border bg-muted border-border dark:bg-card dark:border-border hover:border-primary/60 transition-colors text-left group"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded bg-card border border-border shrink-0">
                <TechIcon icon={iconId} color={iconHex} size={16} alt="" />
              </span>
              <span className="text-sm text-foreground truncate flex-1">Change icon</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
            </button>
          </div>
        )}

        {/* Label */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
            Label
          </label>
          <input
            type="text"
            value={label}
            onChange={handleLabelChange}
            onBlur={submitLabel}
            onKeyDown={handleKeyDown}
            placeholder="Node label"
            className="w-full text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-ring bg-muted border-border dark:bg-card dark:border-border text-foreground dark:text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {/* Description (tech nodes) — shown on the node whenever it's non-empty. */}
        {isTechNode && (
          <div>
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={handleDescriptionChange}
              onBlur={submitDescription}
              onKeyDown={handleDescriptionKeyDown}
              placeholder="Short description (optional)"
              className="w-full text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-ring bg-muted border-border dark:bg-card dark:border-border text-foreground dark:text-foreground placeholder:text-muted-foreground"
            />
          </div>
        )}

        {/* Border Toggle & Size */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Border
            </label>
            <button
              onClick={handleBorderToggle}
              className={`w-full h-8 flex items-center justify-center gap-2 rounded-md border transition-all text-sm ${
                showBorder
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:border-border dark:bg-card dark:border-border'
              }`}
            >
              <Square className="w-3 h-3" />
              {showBorder ? 'On' : 'Off'}
            </button>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Size
            </label>
            <div className="flex bg-muted dark:bg-card p-1 rounded-lg h-8">
              {SIZE_OPTIONS.map((s) => (
                <Tooltip key={s.value} content={s.label} wrapperClassName="flex-1">
                  <button
                    onClick={() => handleSizeChange(s.value)}
                    className={`w-full rounded flex items-center justify-center transition-all text-xs font-medium ${
                      currentSizeMod === s.value
                        ? 'bg-card shadow text-foreground dark:bg-muted dark:text-foreground'
                        : 'text-muted-foreground hover:text-muted-foreground dark:text-muted-foreground dark:hover:text-muted-foreground'
                    }`}
                    aria-label={s.label}
                  >
                    {s.label}
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>

        {/* Node Color */}
        <div>
          <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
            Background Color
          </label>
          <div className="flex flex-wrap gap-2">
            {NODE_COLORS.map((c) => (
              <Tooltip key={c.value || 'default'} content={c.label}>
                <button
                  onClick={() => handleNodeColorChange(c.value)}
                  className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-slate-900 focus:ring-border ${
                    currentNodeColor === c.value
                      ? 'border-border dark:border-white'
                      : 'border-border dark:border-border'
                  }`}
                  style={{
                    background: `linear-gradient(135deg, ${c.lightBg} 50%, ${c.darkBg} 50%)`
                  }}
                  aria-label={c.label}
                />
              </Tooltip>
            ))}
          </div>
        </div>

        {/* Border Color */}
        {showBorder && (
          <div>
            <label className="block text-xs font-medium mb-1.5 text-foreground dark:text-muted-foreground">
              Border Color
            </label>
            <div className="flex flex-wrap gap-2">
              {BORDER_COLORS.map((c) => (
                <Tooltip key={c.value || 'default'} content={c.label}>
                  <button
                    onClick={() => handleBorderColorChange(c.value)}
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-slate-900 focus:ring-border ${
                      currentBorderColor === c.value
                        ? 'border-border dark:border-white'
                        : 'border-transparent'
                    }`}
                    style={{
                      backgroundColor: c.value ? `#${c.value}` : undefined,
                      background: c.value
                        ? `#${c.value}`
                        : 'linear-gradient(135deg, #e2e8f0 50%, #1e293b 50%)',
                    }}
                    aria-label={c.label}
                  />
                </Tooltip>
              ))}
            </div>
          </div>
        )}

        {/* Delete */}
        <button
          onClick={() => onDelete(node.id)}
          className="w-full flex items-center justify-center gap-2 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-md transition-colors"
        >
          <Trash2 className="w-3 h-3" />
          Delete Node
        </button>
      </div>
    </div>
  );
};

export default NodeSettingsPanel;
